/**
 * Help Center Content Structure
 * Role-based content for Readers, Authors, and Publishers
 * 
 * Each section has:
 * - id: unique identifier for routing
 * - title: display title
 * - icon: lucide-react icon name
 * - articles: array of help articles
 * - roles: which user roles can see this section
 */

export const helpCenterContent = {
  // ==================== COMMON SECTIONS (All Roles) ====================
  getting_started: {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'Rocket',
    roles: ['reader', 'author', 'publisher', 'admin'],
    articles: [
      {
        id: 'what-is-newaudia',
        title: 'What is NewAudia?',
        content: `
NewAudia is an innovative audiobook platform that transforms manuscripts into interactive NFD-SmartBook™ experiences with AI-generated narration and an intelligent AI companion named Audia.

## Key Features

- **NFD-SmartBook™ Format** - Our proprietary audio-first, interactive book format that creates a more engaging listening experience
- **AI-Generated Narration** - Professional quality audiobooks without the need for recording studios
- **Audia AI Companion** - Ask questions about any book while listening and get intelligent, context-aware answers
- **Reader Credits System** - Pay only for what you use, with transparent pricing
- **Free to Start** - All new users receive 10 free Introduction Credits
- **Author-Friendly** - Upload your EPUB and get an instant audiobook at no upfront cost
        `,
        relatedLinks: [
          { text: 'Create an Account', path: '/login?mode=register', type: 'app' },
          { text: 'Browse the Library', path: '/public/library', type: 'app' },
          { text: 'Understanding Credits', path: '/help/credits/understanding-credits', type: 'help' }
        ]
      },
      {
        id: 'creating-account',
        title: 'Creating Your Account',
        content: `
Getting started with NewAudia is quick and easy. Follow these steps to create your free account:

## Registration Steps

1. Click **"Get Started Free"** on the homepage or **"Sign Up"** in the navigation bar
2. Fill in your personal information:
   - First Name and Last Name
   - Phone Number
   - Email Address (this will be your login)
   - Alternate Email (for account recovery)
   - Create a secure Password
   - City, State, and Country
3. Click **"Create Account"** to complete registration
4. You'll automatically receive **10 free Introduction Credits** to explore the platform

## Important Notes

- All fields marked with an asterisk (*) are required
- Use a valid email address – you'll need it to sign in
- Your alternate email is used for password recovery
- Passwords must be at least 8 characters

## What Happens Next?

After creating your account, you'll be guided through a brief onboarding process where you can:
- Select your favorite genres
- Choose topics that interest you
- Get personalized book recommendations
        `,
        relatedLinks: [
          { text: 'Sign Up Now', path: '/login?mode=register', type: 'app' },
          { text: 'Account Settings', path: '/help/account/settings', type: 'help' }
        ]
      },
      {
        id: 'signing-in',
        title: 'Signing In',
        content: `
Access your NewAudia account from any device by following these simple steps:

## Sign-In Options

### Email and Password
1. Click **"Sign In"** in the navigation bar
2. Enter your registered email address
3. Enter your password
4. Click the **"Sign In"** button
5. You'll be redirected to your Reader Homepage

### Google Sign-In
NewAudia supports Google Sign-In for quick registration and login:
1. Click **"Sign In"** in the navigation bar
2. Click **"Continue with Google"**
3. Select your Google account
4. You'll be signed in automatically — no separate password needed

## Staying Signed In

- Your session will remain active until you explicitly sign out
- For security, sign out when using shared computers

## Trouble Signing In?

If you're having trouble accessing your account:
- Double-check your email address for typos
- Ensure Caps Lock is off when entering your password
- Try resetting your password if you've forgotten it
- Contact support at info@newaudia.com if issues persist
        `,
        relatedLinks: [
          { text: 'Sign In', path: '/login', type: 'app' },
          { text: 'Reset Password', path: '/help/account/password-reset', type: 'help' },
          { text: 'Contact Support', path: '/contact', type: 'app' }
        ]
      }
    ]
  },

  account: {
    id: 'account',
    title: 'Account & Settings',
    icon: 'Settings',
    roles: ['reader', 'author', 'publisher', 'admin'],
    articles: [
      {
        id: 'settings',
        title: 'Account Settings',
        content: `
Manage your NewAudia account settings to customize your experience.

## Accessing Settings

1. Click your profile icon in the top-right corner
2. Select **"Account"** from the dropdown menu

## What You Can Update

### Personal Information
- First and Last Name
- Phone Number
- Location (City, State, Country)

### Email Settings
- Primary Email (used for login)
- Alternate Email (for recovery)

### Security
- Change your password
- View login history

### Preferences
- Notification settings
- Communication preferences
        `,
        relatedLinks: [
          { text: 'Go to Account Settings', path: '/account', type: 'app' }
        ]
      },
      {
        id: 'password-reset',
        title: 'Resetting Your Password',
        content: `
Forgot your password? No worries – here's how to reset it:

## Password Reset Steps

1. Go to the Sign In page
2. Click **"Forgot Password?"** below the password field
3. Enter your registered email address
4. Click **"Send Reset Link"**
5. Check your inbox for an email from NewAudia
6. Click the reset link in the email
7. Create your new password
8. You can now sign in with your new password

## Important Notes

- The password reset link expires after **24 hours**
- Check your spam/junk folder if you don't see the email
- You can request a new reset link if the first one expires
- Contact info@newaudia.com if you continue to have issues

## Password Requirements

Your new password must:
- Be at least 8 characters long
- Contain at least one uppercase letter
- Contain at least one number
        `,
        relatedLinks: [
          { text: 'Sign In', path: '/login', type: 'app' },
          { text: 'Contact Support', path: '/contact', type: 'app' }
        ]
      }
    ]
  },

  // ==================== READER-SPECIFIC SECTIONS ====================
  browsing: {
    id: 'browsing',
    title: 'Browsing & Discovery',
    icon: 'Search',
    roles: ['reader'],
    articles: [
      {
        id: 'browsing-library',
        title: 'Browsing the Library',
        content: `
Discover your next favorite book using NewAudia's powerful browsing features.

## Homepage Sections

When you visit the Reader Homepage, you'll find several curated sections:

- **New Titles** - Recently added books to the platform
- **Read Free** - Promotional books that don't require credits
- **Personalized For You** - Recommendations based on your preferences
- **Browse by Genre** - Fiction, Non-Fiction, Business, Self-Help, and more
- **Browse by Theme** - Love, Adventure, Technology, Leadership, etc.

## Filtering Books

Each carousel section has dropdown filters:

1. Click the dropdown arrow next to a section title
2. Select a genre or theme to filter
3. The section updates to show matching books
4. Click "All" to see the full selection again

## Book Cards

Each book card shows:
- Cover image
- Title and author
- **"FREE"** badge for promotional books
- Quick action buttons (Add to Library, Read Now)

## Tips for Discovery

- Check "Read Free" regularly for new promotional content
- Complete your onboarding preferences for better recommendations
- Browse multiple genres to expand your listening horizons
        `,
        relatedLinks: [
          { text: 'Go to Reader Home', path: '/home', type: 'app' },
          { text: 'Public Library', path: '/public/library', type: 'app' }
        ]
      },
      {
        id: 'book-details',
        title: 'Viewing Book Details',
        content: `
Learn everything about a book before adding it to your library.

## How to View Details

Click on any book card to open its details page.

## Available Information

- **Cover Image** - High-quality book cover
- **Title & Author** - Full title and author name
- **Description** - Detailed synopsis of the book
- **Genre & Themes** - Categories and subject matter
- **About the Author** - Background on the writer

## Actions You Can Take

- **Add to Library** - Save the book for later reading
- **PLAY** - Start listening immediately
        `,
        relatedLinks: [
          { text: 'My Library', path: '/reader/my-library', type: 'app' },
          { text: 'Understanding Credits', path: '/help/credits/understanding-credits', type: 'help' }
        ]
      }
    ]
  },

  library: {
    id: 'library',
    title: 'My Library',
    icon: 'Library',
    roles: ['reader'],
    articles: [
      {
        id: 'managing-library',
        title: 'Managing Your Library',
        content: `
Your personal library is where you keep all the books you're reading or plan to read.

## Adding Books

1. Browse the library and find a book you like
2. Click on the book card to view details
3. Click **"Add to Library"** button
4. The book now appears in your My Library section

## Accessing My Library

- Click **"My Library"** in the navigation menu
- Or click the Library icon on mobile

## Library Features

- **Progress Tracking** - See how far you are in each book
- **Resume Listening** - Pick up exactly where you left off
- **Remove Books** - Clean up books you've finished
- **Quick Actions** - One-click access to continue listening

## Library Organization

Your library automatically shows:
- Books currently in progress at the top
- Recently added books
- Completed books (optional to show/hide)
        `,
        relatedLinks: [
          { text: 'Go to My Library', path: '/reader/my-library', type: 'app' },
          { text: 'Browse Books', path: '/home', type: 'app' }
        ]
      },
      {
        id: 'continue-reading',
        title: 'Continuing Where You Left Off',
        content: `
NewAudia automatically saves your progress so you never lose your place.

## How It Works

When you return to a book you've already started, you'll see a **"Welcome Back"** prompt with options:

- **Continue Listening** - Resume from your exact last position
- **Start Over** - Begin from Chapter 1

## Progress Saving

Your progress is automatically saved:
- After each chapter
- When you pause playback
- When you close the player
- Across all your devices

## Continue Reading Button

From the Reader Homepage, click **"Continue Reading"** to quickly resume your most recent book.

## Tips

- Your progress syncs across devices automatically
- You can have multiple books in progress at once
- Check "My Library" to see progress bars for all books
        `,
        relatedLinks: [
          { text: 'Reader Homepage', path: '/home', type: 'app' },
          { text: 'My Library', path: '/reader/my-library', type: 'app' }
        ]
      }
    ]
  },

  listening: {
    id: 'listening',
    title: 'Listening & Audio Player',
    icon: 'Headphones',
    roles: ['reader'],
    articles: [
      {
        id: 'audio-player',
        title: 'Using the Audio Player',
        content: `
The NewAudia audio player provides a rich, immersive listening experience.

## Starting Playback

- Click **"PLAY"** on any book details page
- Or click a book in your library to continue
- The audio player opens and playback begins

## Player Controls

| Control | Function |
|---------|----------|
| Play / Pause | Start or pause audio |
| Rewind | Jump back within the current segment |
| Fast Forward | Jump forward within the current segment |
| Previous | Go to previous segment |
| Next | Go to next segment |
| Chapter List | Jump to any chapter or segment |
| Progress Bar | Scrub to any position within the current segment |

## Segment Navigation

Use the progress bar to scrub within the current segment, or open the chapter list to jump to a specific section. The Previous and Next buttons move between segments sequentially.

## Playback Speed

Variable playback speed is not currently available. NewAudia's AI-generated narration is delivered at a standard pace optimized for natural listening and comprehension.

## Additional Features

- **Chapter Navigation** - Quick jump to any chapter using the chapter list
- **Progress Display** - See current position and remaining time
- **Auto-Save** - Your progress is saved automatically as you listen

## Closing the Player

- Click the **X** button to close
- Your progress is automatically saved
- Audio stops when you close the player
        `,
        relatedLinks: [
          { text: 'My Library', path: '/reader/my-library', type: 'app' },
          { text: 'Using Audia AI', path: '/help/listening/using-audia', type: 'help' }
        ]
      },
      {
        id: 'using-audia',
        title: 'Using Audia AI While Listening',
        content: `
Audia is your AI companion who knows everything about the book you're reading.

## What is Audia?

Audia is NewAudia's intelligent AI assistant that can:
- Answer questions about the book you're reading
- Explain characters, plots, and concepts
- Provide summaries of what you've heard
- Offer insights and analysis

## How to Access Audia

While in the audio player:
1. Find the **"Ask Audia"** button
2. Click to open the chat interface
3. Type your question and press send
4. Audia responds with context-aware answers

## Example Questions

- "Who is [character name]?"
- "What happened in the last chapter?"
- "What are the main themes of this book?"
- "Summarize what I just heard"
- "Why did [character] do that?"
- "Can you explain [concept] from the book?"

## Key Features

- **Context-Aware** - Audia knows your current position in the book
- **Spoiler-Free** - Won't reveal content you haven't reached yet
- **Text & Voice** - Responses available in both formats
- **Uses Credits** - Each interaction uses Reader Credits

## Tips for Better Answers

- Be specific in your questions
- Reference characters or events by name when possible
- Ask follow-up questions for more detail
        `,
        relatedLinks: [
          { text: 'Understanding Credits', path: '/help/credits/understanding-credits', type: 'help' },
          { text: 'FAQ', path: '/faq', type: 'app' }
        ]
      }
    ]
  },

  credits: {
    id: 'credits',
    title: 'Credits & Pricing',
    icon: 'Coins',
    roles: ['reader'],
    articles: [
      {
        id: 'understanding-credits',
        title: 'Understanding Reader Credits',
        content: `
Reader Credits are the currency used on NewAudia to access content and features.

## What Are Credits?

Credits are units that allow you to:
- Listen to non-free (paid) audiobooks
- Interact with Audia AI

## Free Credits

Every new account receives **free Introduction Credits** to explore the platform. Visit the Pricing page for current details.

## What Uses Credits?

| Activity | Credit Usage |
|----------|--------------|
| Listening to paid books | Variable based on duration |
| Asking Audia questions | Small amount per interaction |
| Promotional/Free books | **NO credits** |

## Running Out of Credits

If your credit balance reaches zero during playback, the audio will pause and you'll be prompted to purchase additional credits. Your listening progress is saved automatically, so you can resume exactly where you left off after adding credits.

## Checking Your Balance

Your current credit balance is always displayed in the top navigation bar when you're logged in.

## Credits Don't Expire

Once you have credits, they remain in your account until used – they never expire!
        `,
        relatedLinks: [
          { text: 'View Pricing', path: '/pricing', type: 'app' },
          { text: 'Buy Credits', path: '/pricing', type: 'app' }
        ]
      },
      {
        id: 'membership-types',
        title: 'Membership Types',
        content: `
NewAudia offers flexible options to fit your listening style.

## Introduction (Free)

New accounts receive free starter credits to explore the library — no payment required. This lets you try NewAudia before committing to any purchases.

## À La Carte

Purchase credit bundles as needed with no ongoing commitment. Perfect for:
- Occasional listeners
- Those who prefer pay-as-you-go flexibility
- Trying out the platform beyond free credits

## Subscriber

Monthly plans that provide recurring credits and access to subscriber perks. Ideal for:
- Regular listeners
- Those who want credits automatically replenished
- Access to discounted bundle prices

Visit the **Pricing page** for current plan details and pricing.
        `,
        relatedLinks: [
          { text: 'View Pricing', path: '/pricing', type: 'app' },
          { text: 'Subscription Plans', path: '/help/credits/subscriptions', type: 'help' }
        ]
      },
      {
        id: 'buying-credits',
        title: 'Purchasing Credit Bundles',
        content: `
Need more credits? Purchase bundles without committing to a subscription.

## Credit Bundles

NewAudia offers various credit bundle options for one-time purchases. Visit the Pricing page to see current bundle options and pricing.

Subscribers may receive discounted bundle prices.

## How to Purchase

1. Go to the **Pricing** page
2. Scroll to the **"Credit Bundles"** section (or access via the "Buy Credits" button)
3. Click **"Buy"** on your chosen bundle
4. Complete payment through our secure checkout
5. Credits are added to your account immediately

## Payment Methods

We accept all major credit cards, PayPal, Apple Pay, Google Pay, and Cash App Pay, processed securely through Stripe.

## Key Benefits

- No subscription required
- Credits never expire
- Instant delivery to your account
- Buy as many bundles as you need
        `,
        relatedLinks: [
          { text: 'Go to Pricing', path: '/pricing', type: 'app' },
          { text: 'Subscription Plans', path: '/help/credits/subscriptions', type: 'help' }
        ]
      },
      {
        id: 'subscriptions',
        title: 'Subscription Plans',
        content: `
Subscribe for monthly credit allotments and additional benefits.

## Available Plans

NewAudia offers several subscription tiers designed for different listening habits. Visit the Pricing page to see current plan details and pricing.

## Subscription Benefits

- Monthly credit allotment automatically added to your account
- Discounted prices on credit bundles
- Cancel anytime with no penalties
- Annual plans available at a discount
- Credits never expire

## How to Subscribe

1. Go to the **Pricing** page
2. Review subscription options and compare tiers
3. Click **"Subscribe"** on your chosen plan
4. Complete payment through our secure checkout
5. Credits are added to your account immediately and monthly thereafter

## Managing Your Subscription

- Upgrade or downgrade anytime from Account Settings
- Cancel anytime – unused credits remain in your account
- Credits never expire, even after cancellation
        `,
        relatedLinks: [
          { text: 'View Pricing', path: '/pricing', type: 'app' },
          { text: 'Account Settings', path: '/account', type: 'app' }
        ]
      }
    ]
  },

  // ==================== AUTHOR-SPECIFIC SECTIONS ====================
  author_getting_started: {
    id: 'author-getting-started',
    title: 'Author Quick Start',
    icon: 'PenTool',
    roles: ['author', 'publisher'],
    articles: [
      {
        id: 'becoming-author',
        title: 'Becoming a NewAudia Author',
        content: `
Transform your manuscript into an AI-powered audiobook with NewAudia.

## Why Publish on NewAudia?

- **No Upfront Costs** - We handle audio generation at no charge to you
- **AI Narration** - Professional quality without hiring voice actors
- **Quick Publishing** - Go from manuscript to live audiobook in under an hour
- **Retain Rights** - You keep full rights to your work
- **Earn Revenue** - Share in Reader Credit consumption

## Getting Started

1. **Create an Account** - Sign up for free at NewAudia
2. **Access Creator Console** - Navigate to your Author Dashboard
3. **Complete Your Profile** - Add author bio and profile image
4. **Upload Your First Book** - Follow our simple upload process

## What You'll Need

- Your manuscript in **EPUB format**
- A book cover image (recommended)
- Book description and metadata
- Genre and theme selections
        `,
        relatedLinks: [
          { text: 'Author Dashboard', path: '/author', type: 'app' },
          { text: 'Publishing Your Book', path: '/help/author-publishing/upload-process', type: 'help' }
        ]
      },
      {
        id: 'author-profile',
        title: 'Setting Up Your Author Profile',
        content: `
A complete author profile helps readers connect with you and your work.

## Accessing Your Profile

1. Go to your **Author Dashboard**
2. Click **"Edit Profile"** or find the profile card

## Profile Elements

### Author Information
- **Pen Name** - Your public author name
- **Biography** - Tell readers about yourself
- **Headshot** - Professional author photo

### Publisher Information (Optional)
- **Publisher Name** - Your imprint or publishing company
- **Publisher Logo** - Brand logo image

## Tips for a Great Profile

- Write a compelling bio that connects with readers
- Use a high-quality, professional headshot
- Keep your bio updated with new releases
- Mention your writing background and inspirations
        `,
        relatedLinks: [
          { text: 'Author Dashboard', path: '/author', type: 'app' }
        ]
      }
    ]
  },

  author_publishing: {
    id: 'author-publishing',
    title: 'Publishing Your Book',
    icon: 'BookUp',
    roles: ['author', 'publisher'],
    articles: [
      {
        id: 'upload-process',
        title: 'The Upload Process',
        content: `
Follow these steps to publish your book on NewAudia.

## Step 1: Prepare Your Manuscript

- Export your manuscript as **EPUB format**
- **Maximum file size: 20 MB**
- If your manuscript exceeds this limit, consider compressing embedded images or splitting the work into multiple volumes
- Ensure chapter structure is correct in the EPUB
- Include a title page and chapter headings

## Step 2: Start the Upload

1. Go to your **Author Dashboard**
2. Click **"Add a Book to Your Library"** or **"Create New Title"**
3. Select your EPUB file
4. Wait for the file to upload and process

## Step 3: Review the Outline

- Our system automatically detects chapters
- Use the **Outline Editor** to review and customize
- Rename chapters if needed
- Split or merge sections as desired

## Step 4: Add Metadata

- **Title** - Your book's title
- **Description** - Engaging book summary
- **Cover Image** - Upload a compelling cover
- **Genre** - Select primary category
- **Themes** - Add relevant topic tags

## Step 5: Generate Audio

- Choose to generate AI narration
- Preview sample chapters
- Full generation takes 30-60 minutes

## Step 6: Activate

- Review your book details
- Click **"Activate"** to publish
- Your book is now live in the public library!

## Processing Time

The entire process typically takes 30-60 minutes, with most of that time being audio generation running in the background.
        `,
        relatedLinks: [
          { text: 'Author Dashboard', path: '/author', type: 'app' },
          { text: 'Managing Your Books', path: '/help/author-publishing/managing-books', type: 'help' }
        ]
      },
      {
        id: 'managing-books',
        title: 'Managing Your Books',
        content: `
Once published, you can manage and update your books at any time.

## Viewing Your Books

Your Author Dashboard shows all your uploaded manuscripts with:
- Cover image
- Title and status
- Upload date
- Quick access to details

## Book Actions

Click on any book to access:

- **View Details** - See full book information
- **Edit Metadata** - Update title, description, cover
- **View Analytics** - See engagement metrics
- **Deactivate** - Remove from public library
- **Reactivate** - Make available again

## Status Meanings

| Status | Meaning |
|--------|---------|
| Active | Live and available to readers |
| Ready | Processed but not yet activated |
| Audio Pending | Audio generation in progress |
| Inactive | Removed from public library |
| Draft | Not yet completed |

## Updating Your Book

You can update metadata at any time:
- Edit title and description
- Replace cover image
- Modify genre and themes
- Changes take effect immediately
        `,
        relatedLinks: [
          { text: 'Author Dashboard', path: '/author', type: 'app' },
          { text: 'Author Analytics', path: '/help/author-publishing/analytics', type: 'help' }
        ]
      },
      {
        id: 'analytics',
        title: 'Understanding Author Analytics',
        content: `
Track how readers engage with your books through the analytics dashboard.

## Available Metrics

### Audience Metrics
- **Total Listeners** - Unique readers who've accessed your book
- **Active Readers** - Currently reading

### Engagement Metrics
- **Total Listening Time** - Combined hours across all readers
- **Chapter Completion Rates** - Which chapters readers finish
- **Audia Interactions** - Questions asked about your book

### Revenue Metrics
- **Credits Consumed** - Reader Credits used on your book
- **Estimated Earnings** - Your revenue share

## Accessing Analytics

1. Go to your **Author Dashboard**
2. Click on a specific book
3. View the analytics section

## Using Analytics

- Identify which books perform best
- See where readers engage most
- Understand reader behavior patterns
- Track growth over time
        `,
        relatedLinks: [
          { text: 'Author Dashboard', path: '/author', type: 'app' }
        ]
      }
    ]
  },

  // ==================== PUBLISHER-SPECIFIC SECTIONS ====================
  publisher_management: {
    id: 'publisher-management',
    title: 'Publisher Management',
    icon: 'Building2',
    roles: ['publisher'],
    articles: [
      {
        id: 'publisher-overview',
        title: 'Publisher Dashboard Overview',
        content: `
The Publisher Dashboard is your command center for managing multiple authors and titles.

## Dashboard Features

- **Author Management** - Create and manage author profiles
- **Title Management** - Upload and manage books for your authors
- **Portfolio Analytics** - See performance across all titles
- **Bulk Operations** - Manage multiple items efficiently

## Accessing the Dashboard

1. Sign in to your publisher account
2. Navigate to **Publisher Console** from the menu
3. View your portfolio overview

## Key Sections

### Authors
- View all represented authors
- Add new author profiles
- Edit author information
- Access individual author consoles

### Titles
- See all books in your catalog
- Filter by author, status, or date
- Quick access to book management

### Analytics
- Portfolio-wide metrics
- Author performance comparison
- Revenue tracking
        `,
        relatedLinks: [
          { text: 'Publisher Dashboard', path: '/publisher', type: 'app' },
          { text: 'Managing Authors', path: '/help/publisher-management/managing-authors', type: 'help' }
        ]
      },
      {
        id: 'managing-authors',
        title: 'Managing Author Profiles',
        content: `
Create and manage author profiles for writers in your publishing house.

## Creating an Author Profile

1. Go to **Publisher Dashboard**
2. Click **"Add Author"** or **"Create New Author"**
3. Fill in author details:
   - Author name (pen name)
   - Biography
   - Contact information
   - Profile image
4. Save the profile

## Managing Existing Authors

From the Authors list:
- Click an author to view their profile
- Edit author information
- Access their book catalog
- View their analytics

## Author Console Access

For each author, you can:
- Access their dedicated console
- Upload books on their behalf
- Manage their titles
- View their earnings

## Best Practices

- Keep author bios updated
- Use professional headshots
- Ensure contact info is current
- Regularly review author analytics
        `,
        relatedLinks: [
          { text: 'Publisher Dashboard', path: '/publisher', type: 'app' },
          { text: 'View Authors', path: '/publisher/authors', type: 'app' }
        ]
      },
      {
        id: 'bulk-uploads',
        title: 'Publishing for Multiple Authors',
        content: `
Efficiently manage book uploads across your author roster.

## Publishing for an Author

1. Go to **Publisher Dashboard**
2. Select the author from your list
3. Click **"Add Book"** or access their console
4. Follow the standard upload process
5. The book is associated with that author's profile

## Workflow Tips

- Prepare all EPUB files in advance
- Have metadata ready (descriptions, covers)
- Process books in batches by author
- Use the outline editor efficiently

## Tracking Progress

Monitor uploads across authors:
- Dashboard shows processing status
- Filter by author to see their books
- View which titles are pending vs. active
        `,
        relatedLinks: [
          { text: 'Publisher Dashboard', path: '/publisher', type: 'app' },
          { text: 'Upload Process', path: '/help/author-publishing/upload-process', type: 'help' }
        ]
      }
    ]
  },

  // ==================== SUPPORT SECTION ====================
  support: {
    id: 'support',
    title: 'Support & Contact',
    icon: 'LifeBuoy',
    roles: ['reader', 'author', 'publisher', 'admin'],
    articles: [
      {
        id: 'getting-help',
        title: 'Getting Help',
        content: `
Need assistance? Here are all the ways to get help with NewAudia.

## Self-Service Options

### Ask Audia
Use our AI assistant for quick answers:
- Click **"Ask Audia"** from the Help Center
- Available on most pages via the help button
- Get instant answers to common questions

### Help Center
You're here! Browse our comprehensive documentation for:
- Step-by-step guides
- Feature explanations
- Troubleshooting tips

### FAQ
Visit our **Frequently Asked Questions** page for quick answers to common queries.

## Contact Support

For issues that need human assistance:

**Email:** info@newaudia.com

**Response Time:** We aim to respond within 24-48 hours

## What to Include

When contacting support, please provide:
- Your account email
- Description of the issue
- Steps to reproduce (if applicable)
- Any error messages you've seen
- Screenshots (if helpful)
        `,
        relatedLinks: [
          { text: 'Contact Us', path: '/contact', type: 'app' },
          { text: 'FAQ', path: '/faq', type: 'app' }
        ]
      },
      {
        id: 'troubleshooting',
        title: 'Common Issues & Solutions',
        content: `
Quick fixes for common issues you might encounter.

## Audio Won't Play

1. Check your internet connection
2. Refresh the page
3. Try a different browser
4. Clear your browser cache
5. Ensure your browser allows audio playback

## Can't Sign In

1. Verify your email address is correct
2. Check Caps Lock is off
3. Try resetting your password
4. Clear browser cookies
5. Contact support if issues persist

## Credits Not Showing

1. Refresh the page
2. Sign out and sign back in
3. Check your purchase confirmation
4. Allow a few minutes after purchase
5. Contact support with payment receipt

## Book Not Loading

1. Check your internet connection
2. Try closing and reopening the book
3. Clear browser cache
4. Try a different browser
5. Report the issue if it continues

## Upload Fails

1. Verify your file is EPUB format
2. Check file size is under 20MB
3. Try uploading again
4. Try a different browser
5. Contact support with the file details
        `,
        relatedLinks: [
          { text: 'Contact Support', path: '/contact', type: 'app' }
        ]
      }
    ]
  }
};

/**
 * Get sections visible to a specific role
 */
export const getSectionsForRole = (userRole) => {
  const role = userRole?.toLowerCase() || 'reader';
  return Object.values(helpCenterContent).filter(section => 
    section.roles.includes(role) || section.roles.includes('admin')
  );
};

/**
 * Get a specific article by section and article ID
 */
export const getArticle = (sectionId, articleId) => {
  const section = Object.values(helpCenterContent).find(s => s.id === sectionId);
  if (!section) return null;
  return section.articles.find(a => a.id === articleId) || null;
};

/**
 * Search articles by query
 */
export const searchArticles = (query, userRole) => {
  const sections = getSectionsForRole(userRole);
  const results = [];
  const queryLower = query.toLowerCase();

  sections.forEach(section => {
    section.articles.forEach(article => {
      const titleMatch = article.title.toLowerCase().includes(queryLower);
      const contentMatch = article.content.toLowerCase().includes(queryLower);
      
      if (titleMatch || contentMatch) {
        results.push({
          sectionId: section.id,
          sectionTitle: section.title,
          articleId: article.id,
          articleTitle: article.title,
          excerpt: getExcerpt(article.content, queryLower),
          relevance: titleMatch ? 2 : 1
        });
      }
    });
  });

  // Sort by relevance (title matches first)
  return results.sort((a, b) => b.relevance - a.relevance);
};

/**
 * Get excerpt with search term highlighted context
 */
const getExcerpt = (content, query) => {
  const plainText = content.replace(/[#*`|]/g, '').replace(/\n+/g, ' ').trim();
  const index = plainText.toLowerCase().indexOf(query);
  
  if (index === -1) {
    return plainText.substring(0, 150) + '...';
  }
  
  const start = Math.max(0, index - 50);
  const end = Math.min(plainText.length, index + query.length + 100);
  
  let excerpt = '';
  if (start > 0) excerpt += '...';
  excerpt += plainText.substring(start, end);
  if (end < plainText.length) excerpt += '...';
  
  return excerpt;
};

export default helpCenterContent;
